import { useMemo } from 'react'
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useReleaseCheckItems, useReleaseCheckStatus } from '../../api/release-check-api'
import { CheckCategory, Discrepancy, ItemStatus, ReleaseCheckItem } from '../../types/ReleaseCheckItem'
import { isItemVisible } from './itemVisibility'
import { usePanelState } from './usePanelState'

const statusColor = (s: ItemStatus): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch (s) {
    case 'Ok':
      return 'success'
    case 'DiscrepanciesFound':
      return 'warning'
    case 'TargetMissing':
    case 'Failed':
      return 'error'
    case 'Pending':
    default:
      return 'info'
  }
}

interface ResultRowProps {
  item: ReleaseCheckItem
  showCategory: boolean
  showStatus: boolean
  controlEnv: string
  targetEnv: string
}

interface DiscrepancyLineProps {
  d: Discrepancy
  controlEnv: string
  targetEnv: string
}

const DiscrepancyLine = ({ d, controlEnv, targetEnv }: DiscrepancyLineProps) => {
  const { t } = useTranslation()
  // Translation strings may reference {{controlEnv}} / {{targetEnv}}; ones that don't just ignore them.
  const label = t(`releaseCheck.discrepancy.${d.category}`, {
    defaultValue: d.category,
    controlEnv,
    targetEnv,
  })
  return (
    <Typography variant="body2" component="div">
      {label}: <code>{d.key}</code>
    </Typography>
  )
}

const ResultRow = ({ item, showCategory, showStatus, controlEnv, targetEnv }: ResultRowProps) => {
  const { t } = useTranslation()
  const isTargetMissing = item.status === 'TargetMissing'

  return (
    <TableRow hover sx={{ opacity: item.ignored ? 0.55 : 1, verticalAlign: 'top' }}>
      {showCategory && (
        <TableCell>{t(`releaseCheck.category.${item.category}`, item.category)}</TableCell>
      )}
      <TableCell sx={{ minWidth: 220, whiteSpace: 'nowrap' }}>
        <Typography variant="body2">{item.targetName ?? item.controlName}</Typography>
      </TableCell>
      {showStatus && (
        <TableCell>
          <Chip
            size="small"
            label={t(`releaseCheck.status.${item.status}`, item.status)}
            color={statusColor(item.status)}
          />
        </TableCell>
      )}
      <TableCell>
        {item.error && (
          <Alert severity="error" sx={{ mb: item.discrepancies.length > 0 || isTargetMissing ? 1 : 0 }}>
            {item.error}
          </Alert>
        )}
        {isTargetMissing && (
          <Typography variant="body2" component="div" color="error">
            <strong>[{t('releaseCheck.status.TargetMissing', 'Target missing')}]</strong>
            {item.targetName ? ` — ${item.targetName}` : ''}
          </Typography>
        )}
        {item.discrepancies.map((d, i) => (
          <DiscrepancyLine key={i} d={d} controlEnv={controlEnv} targetEnv={targetEnv} />
        ))}
        {!item.error && !isTargetMissing && item.discrepancies.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {item.discrepancies.length > 0 ? item.discrepancies.length : '—'}
      </TableCell>
      <TableCell>
        {item.lastScanDate ? new Date(item.lastScanDate).toLocaleString() : '—'}
      </TableCell>
      <TableCell>
        {item.ignored && (
          <Chip size="small" variant="outlined" label={t('releaseCheck.ignored', 'Ignored')} />
        )}
      </TableCell>
    </TableRow>
  )
}

interface ResultsTableProps {
  // Omit to show every category combined (the "All" panel).
  category?: CheckCategory
}

const ResultsTable = ({ category }: ResultsTableProps) => {
  const { t } = useTranslation()
  const [items, isLoading] = useReleaseCheckItems()
  const [status] = useReleaseCheckStatus()
  const controlEnv = status?.controlEnv ?? ''
  const targetEnv = status?.targetEnv ?? ''
  const { includeIgnored, includeCompliant, setIncludeIgnored, setIncludeCompliant } =
    usePanelState()

  const showStatus = includeIgnored || includeCompliant
  const showCategory = !category

  const visible = useMemo(
    () =>
      items.filter(
        (it) =>
          (!category || it.category === category) &&
          isItemVisible(it, includeIgnored, includeCompliant),
      ),
    [items, category, includeIgnored, includeCompliant],
  )

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeIgnored}
              onChange={(_, c) => setIncludeIgnored(c)}
              size="small"
            />
          }
          label={t('releaseCheck.includeIgnored', 'Include ignored')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={includeCompliant}
              onChange={(_, c) => setIncludeCompliant(c)}
              size="small"
            />
          }
          label={t('releaseCheck.includeCompliant', 'Include compliant')}
        />
      </Stack>

      {visible.length === 0 ? (
        <Alert severity="success">
          {t('releaseCheck.noDiscrepancies', 'No discrepancies to display.')}
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {showCategory && (
                  <TableCell>{t('releaseCheck.column.category', 'Category')}</TableCell>
                )}
                <TableCell sx={{ minWidth: 220 }}>
                  {t('releaseCheck.column.resource', 'Resource')}
                </TableCell>
                {showStatus && (
                  <TableCell>{t('releaseCheck.column.status', 'Status')}</TableCell>
                )}
                <TableCell>
                  {t('releaseCheck.column.discrepancies', 'Discrepancies')}
                </TableCell>
                <TableCell align="right">
                  {t('releaseCheck.column.findings', 'Findings')}
                </TableCell>
                <TableCell>{t('releaseCheck.column.lastScan', 'Last scan')}</TableCell>
                <TableCell>{t('releaseCheck.column.flags', '')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((it) => (
                <ResultRow
                  key={`${it.category}:${it.id}`}
                  item={it}
                  showCategory={showCategory}
                  showStatus={showStatus}
                  controlEnv={controlEnv}
                  targetEnv={targetEnv}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default ResultsTable
